import { doodBlockConfig, propertyValue } from "./dood-block-config";
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from "./descriptor-field-order";
import type { IBlockDescriptor } from "./descriptor";

export const KIOSKHEADERBLOCK_LEGACY_FIELD_ORDER = [
  "header_landing_text",
  { type: "separator" },
  "background_image",
  "background_url",
  "background_color",
  { type: "separator" },
  "logo",
  "logo_url",
  "logo_width",
  "size",
  { type: "separator" },
  "blocks",
  { type: "separator" },
  "display_home_button",
  "display_back_button",
  "back_button_icon_color",
  "display_leaving_session_button",
  "display_allergen_and_tag_filters",
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  kioskHeaderBlockDescriptor: _generatedKioskHeaderBlockDescriptor,
  kioskHeaderBlockBlocDefinition,
  kioskHeaderBlockDashboardLayout,
  kioskHeaderBlockConfig,
  kioskHeaderBlockResolveConfig,
  kioskHeaderBlockProperties,
} = doodBlockConfig(
  {
    name: "Kiosk Header",
    componentType: "KioskHeaderBlockComponent",
    category: "kiosk",
    description: "Kiosk Header.",
    keywords: [],
    allowed_in: [
      "GenericPage",
      "KioskShopPage",
      "KioskCartPage",
      "KioskCartFunnelPage",
      "KioskPromoSelectProductPage",
      "KioskLoyaltyConnectionPage",
      "KioskPaymentMethodPickerBlock",
      "KioskDistributionModePickerPage",
    ],
    sharedFields: {},
    slots: [
      {
        key: "blocks",
        name: "pages.blocks.kiosk-header.blocks.label",
        accept: "explicitOnly",
        allowedTypes: ["CarouselBlock"],
        slotItemFormat: "ids",
        max: 1,
      },
    ],
  },

  () => ({
    header_landing_text: propertyValue({
      type: "text",
      value: "",
      options: {
        label: "pages.blocks.kiosk-header.header-landing-text.label",
        placeholder:
          "pages.blocks.kiosk-header.header-landing-text.placeholder",
      },
    }),
    background_image: propertyValue({
      type: "image",
      value: "",
      options: {
        label: "pages.blocks.kiosk-header.background-image.label",
        help: "pages.blocks.kiosk-header.background-image.help",
      },
    }),
    background_url: propertyValue({
      type: "link",
      value: "",
      options: {
        label: "pages.blocks.kiosk-header.background-url.label",
        help: "pages.blocks.kiosk-header.background-url.help",
      },
    }),
    background_color: propertyValue({
      type: "color",
      value: { type: "hex", value: "transparent" },
      options: {
        label: "pages.blocks.kiosk-header.background-color.label",
      },
    }),
    logo: propertyValue({
      type: "image",
      value: "",
      options: {
        label: "pages.blocks.kiosk-header.logo.label",
        help: "pages.blocks.kiosk-header.logo.help",
      },
    }),
    logo_url: propertyValue({
      type: "link",
      value: "",
      options: {
        label: "pages.blocks.kiosk-header.logo-url.label",
        help: "pages.blocks.kiosk-header.logo-url.help",
      },
    }),
    logo_width: propertyValue({
      type: "select",
      value: "medium",
      options: {
        label: "pages.blocks.kiosk-header.logo-width.label",
        choices: [
          {
            value: "small",
            label: "pages.blocks.kiosk-header.logo-width.options.small",
          },
          {
            value: "medium",
            label: "pages.blocks.kiosk-header.logo-width.options.medium",
          },
          {
            value: "large",
            label: "pages.blocks.kiosk-header.logo-width.options.large",
          },
        ],
      },
    }),
    size: propertyValue({
      type: "select",
      value: "S",
      options: {
        label: "pages.blocks.kiosk-header.size.label",
        choices: [
          {
            value: "L",
            label: "pages.blocks.kiosk-header.size.options.L",
          },
          {
            value: "M",
            label: "pages.blocks.kiosk-header.size.options.M",
          },
          {
            value: "S",
            label: "pages.blocks.kiosk-header.size.options.S",
          },
        ],
      },
    }),
    display_home_button: propertyValue({
      type: "checkbox",
      value: false,
      options: {
        label: "pages.blocks.kiosk-header.display-home-button.label",
        help: "pages.blocks.kiosk-header.display-home-button.help",
      },
    }),
    display_back_button: propertyValue({
      type: "checkbox",
      value: false,
      options: {
        label: "pages.blocks.kiosk-header.display-back-button.label",
        help: "pages.blocks.kiosk-header.display-back-button.help",
      },
    }),
    back_button_icon_color: propertyValue({
      type: "color",
      value: { type: "hex", value: "#171717" },
      options: {
        label: "pages.blocks.kiosk-header.back-button-icon-color.label",
      },
    }),
    display_leaving_session_button: propertyValue({
      type: "checkbox",
      value: false,
      options: {
        label: "pages.blocks.kiosk-header.display-leaving-session-button.label",
      },
    }),
    display_allergen_and_tag_filters: propertyValue({
      type: "checkbox",
      value: false,
      options: {
        label:
          "pages.blocks.kiosk-header.display-allergen-and-tag-filters.label",
        help: "pages.blocks.kiosk-header.display-allergen-and-tag-filters.help",
      },
    }),
  }),

  () => ({
    tabs: [
      {
        label: "pages.blocks.kiosk-header.tab.content.label",
        fieldsets: [
          {
            legend: "pages.blocks.kiosk-header.fieldset.welcome-text.label",
            fields: ["header_landing_text"],
          },
          {
            legend: "pages.blocks.kiosk-header.fieldset.background.label",
            fields: [
              {
                prop: "background_image",
                visibleWhen: {
                  anyOf: [
                    { prop: "logo" as const, nonEmpty: true },
                    { prop: "logo_url" as const, nonEmpty: true },
                  ],
                },
              },
              {
                prop: "background_url",
                visibleWhen: {
                  anyOf: [
                    { prop: "logo" as const, nonEmpty: true },
                    { prop: "logo_url" as const, nonEmpty: true },
                  ],
                },
              },
              {
                prop: "background_color",
                visibleWhen: {
                  anyOf: [
                    { prop: "logo" as const, nonEmpty: true },
                    { prop: "logo_url" as const, nonEmpty: true },
                  ],
                },
              },
            ],
          },
          {
            legend: "pages.blocks.kiosk-header.fieldset.logo.label",
            fields: ["logo", "logo_url", "logo_width", "size"],
          },
          {
            legend: "pages.blocks.kiosk-header.fieldset.carousel.label",
            fields: ["blocks"],
          },
          {
            legend: "pages.blocks.kiosk-header.fieldset.buttons.label",
            fields: [
              "display_home_button",
              "display_back_button",
              {
                prop: "back_button_icon_color",
                visibleWhen: { display_back_button: true },
              },
              "display_leaving_session_button",
              "display_allergen_and_tag_filters",
            ],
          },
        ],
      },
    ],
  }),
);

export function kioskHeaderBlockDescriptor(): IBlockDescriptor;
export function kioskHeaderBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function kioskHeaderBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedKioskHeaderBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(
    _generatedKioskHeaderBlockDescriptor,
    kioskHeaderBlockProperties,
    fieldOrder,
  );
}

export {
  kioskHeaderBlockBlocDefinition,
  kioskHeaderBlockDashboardLayout,
  kioskHeaderBlockProperties,
  kioskHeaderBlockConfig,
  kioskHeaderBlockResolveConfig,
};

export type KioskHeaderBlockConfig = typeof kioskHeaderBlockConfig;
